import { useCallback, useEffect, useState } from 'react'
import {
  attendanceDeleteTerminal,
  attendanceLoadTerminals,
  attendanceSaveTerminal,
  type AttendanceTerminal,
} from '../../lib/apiPhpAdapter'
import './AttendanceOrgAdmin.css'

type Props = {
  busy: boolean
  setBusy: (v: boolean) => void
  showAlert: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

const MFR_LABELS: Record<string, string> = {
  generic: 'Obecný / middleware',
  zkteco: 'ZKTeco',
  anviz: 'Anviz',
  hikvision: 'Hikvision',
  suprema: 'Suprema',
  other: 'Jiný',
}

const MODE_LABELS: Record<string, string> = {
  webhook_push: 'Webhook (terminál/middleware posílá do intranetu)',
  http_pull: 'HTTP pull (intranet čte ze zařízení)',
  sdk_bridge: 'Lokální bridge / SDK',
}

const emptyForm = (): Partial<AttendanceTerminal> & {
  name: string
  password: string
  rotate_api_token: boolean
} => ({
  name: '',
  manufacturer: 'generic',
  connection_mode: 'webhook_push',
  building_code: 'X',
  host: '',
  port: null,
  api_base_url: '',
  username: '',
  password: '',
  notes: '',
  active: 1,
  rotate_api_token: false,
})

export function AttendanceTerminalsPanel({ busy, setBusy, showAlert }: Props) {
  const [rows, setRows] = useState<AttendanceTerminal[]>([])
  const [manufacturers, setManufacturers] = useState<string[]>(Object.keys(MFR_LABELS))
  const [modes, setModes] = useState<string[]>(Object.keys(MODE_LABELS))
  const [webhookPath, setWebhookPath] = useState('/api/attendance-terminal-punch')
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState(emptyForm)
  const [revealedToken, setRevealedToken] = useState<string | null>(null)

  const reload = useCallback(async () => {
    const res = await attendanceLoadTerminals()
    setRows(Array.isArray(res.terminals) ? res.terminals : [])
    if (Array.isArray(res.manufacturers) && res.manufacturers.length) setManufacturers(res.manufacturers)
    if (Array.isArray(res.connection_modes) && res.connection_modes.length) setModes(res.connection_modes)
    if (res.webhook_path) setWebhookPath(res.webhook_path)
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    reload()
      .catch((e: unknown) => {
        if (!cancelled) showAlert(e instanceof Error ? e.message : 'Terminály se nepodařilo načíst', 'error')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [reload, showAlert])

  const startCreate = () => {
    setEditingId(null)
    setForm(emptyForm())
    setRevealedToken(null)
  }

  const startEdit = (t: AttendanceTerminal) => {
    setEditingId(t.id)
    setForm({
      name: t.name,
      manufacturer: t.manufacturer || 'generic',
      connection_mode: t.connection_mode || 'webhook_push',
      building_code: t.building_code || 'X',
      host: t.host || '',
      port: t.port ?? null,
      api_base_url: t.api_base_url || '',
      username: t.username || '',
      password: '',
      notes: t.notes || '',
      active: t.active ? 1 : 0,
      rotate_api_token: false,
    })
    setRevealedToken(null)
  }

  const save = () => {
    void (async () => {
      if (!form.name.trim()) {
        showAlert('Zadejte název terminálu', 'info')
        return
      }
      setBusy(true)
      try {
        const payload = {
          name: form.name.trim(),
          manufacturer: form.manufacturer || 'generic',
          connection_mode: form.connection_mode || 'webhook_push',
          building_code: (form.building_code || 'X').trim() || 'X',
          host: (form.host || '').trim() || undefined,
          port: form.port || undefined,
          api_base_url: (form.api_base_url || '').trim() || undefined,
          username: (form.username || '').trim() || undefined,
          notes: (form.notes || '').trim() || undefined,
          active: (form.active ? 1 : 0) as 0 | 1,
          rotate_api_token: !!form.rotate_api_token,
          ...(form.password ? { password: form.password } : {}),
        }
        const saved = await attendanceSaveTerminal(payload, editingId ?? undefined)
        showAlert(editingId ? 'Terminál uložen' : 'Terminál vytvořen', 'success')
        if (saved.api_token) setRevealedToken(saved.api_token)
        await reload()
        if (!editingId && saved.id) {
          setEditingId(saved.id)
          setForm((f) => ({ ...f, rotate_api_token: false, password: '' }))
        }
      } catch (e: unknown) {
        showAlert(e instanceof Error ? e.message : 'Uložení selhalo', 'error')
      } finally {
        setBusy(false)
      }
    })()
  }

  const remove = (t: AttendanceTerminal) => {
    if (!window.confirm(`Smazat terminál „${t.name}“?`)) return
    void (async () => {
      setBusy(true)
      try {
        await attendanceDeleteTerminal(t.id)
        if (editingId === t.id) startCreate()
        showAlert('Terminál smazán', 'success')
        await reload()
      } catch (e: unknown) {
        showAlert(e instanceof Error ? e.message : 'Smazání selhalo', 'error')
      } finally {
        setBusy(false)
      }
    })()
  }

  const webhookUrl =
    typeof window !== 'undefined' ? `${window.location.origin}${webhookPath}` : webhookPath

  return (
    <div className="att-cat">
      <p className="att-cat__intro">
        Evidence docházkových terminálů různých výrobců a způsob jejich napojení na intranet. U režimu
        webhook terminál (nebo lokální bridge) posílá události na URL níže s API tokenem.
      </p>

      <div className="att-leave-form__filters" style={{ marginBottom: 10 }}>
        <button type="button" className="system-button att-cat__btn" disabled={busy} onClick={startCreate}>
          Nový terminál
        </button>
        <span className="attendance-muted" style={{ fontSize: '0.82rem' }}>
          Webhook: <code>{webhookUrl}</code>
        </span>
      </div>

      <div className="att-org__dual" style={{ alignItems: 'start' }}>
        <div className="att-org__box">
          <div className="att-org__box-head">
            <strong>Terminály</strong>
            <span className="attendance-muted">{loading ? '…' : rows.length}</span>
          </div>
          <div className="att-cat__wrap" style={{ maxHeight: '52vh' }}>
            <table className="att-cat__table">
              <thead>
                <tr>
                  <th>Název</th>
                  <th>Výrobce</th>
                  <th>Budova</th>
                  <th>Režim</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr
                    key={t.id}
                    className={!t.active ? 'att-cat__row--inactive' : undefined}
                    style={{ cursor: 'pointer' }}
                    onClick={() => startEdit(t)}
                  >
                    <td className="att-cat__col-name">{t.name}</td>
                    <td>{MFR_LABELS[t.manufacturer] || t.manufacturer}</td>
                    <td>{t.building_code}</td>
                    <td style={{ whiteSpace: 'normal', fontSize: '0.78rem' }}>
                      {MODE_LABELS[t.connection_mode] || t.connection_mode}
                    </td>
                    <td className="att-cat__col-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="system-button att-cat__btn att-cat__btn--danger"
                        disabled={busy}
                        onClick={() => remove(t)}
                      >
                        Smazat
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="att-cat__empty">
                      Zatím žádný terminál.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        <div className="att-org__box">
          <div className="att-org__box-head">
            <strong>{editingId ? `Upravit #${editingId}` : 'Nový terminál'}</strong>
          </div>
          <div className="att-org__regime-form" style={{ display: 'grid', gap: 8 }}>
            <label>
              Název
              <input
                value={form.name}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </label>
            <label>
              Výrobce
              <select
                value={form.manufacturer || 'generic'}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, manufacturer: e.target.value }))}
              >
                {manufacturers.map((m) => (
                  <option key={m} value={m}>
                    {MFR_LABELS[m] || m}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Režim připojení
              <select
                value={form.connection_mode || 'webhook_push'}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, connection_mode: e.target.value }))}
              >
                {modes.map((m) => (
                  <option key={m} value={m}>
                    {MODE_LABELS[m] || m}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Kód budovy u punchů
              <input
                value={form.building_code || ''}
                disabled={busy}
                placeholder="K-29"
                maxLength={32}
                onChange={(e) => setForm((f) => ({ ...f, building_code: e.target.value }))}
              />
            </label>
            <label>
              Host / IP zařízení
              <input
                value={form.host || ''}
                disabled={busy}
                placeholder="192.168.1.50"
                onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))}
              />
            </label>
            <label>
              Port
              <input
                type="number"
                value={form.port ?? ''}
                disabled={busy}
                placeholder="4370"
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    port: e.target.value === '' ? null : parseInt(e.target.value, 10) || null,
                  }))
                }
              />
            </label>
            <label>
              API URL (pull / bridge)
              <input
                value={form.api_base_url || ''}
                disabled={busy}
                placeholder="http://192.168.1.50/api"
                onChange={(e) => setForm((f) => ({ ...f, api_base_url: e.target.value }))}
              />
            </label>
            <label>
              Uživatelské jméno
              <input
                value={form.username || ''}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
              />
            </label>
            <label>
              Heslo {editingId ? '(nechte prázdné = beze změny)' : ''}
              <input
                type="password"
                value={form.password}
                disabled={busy}
                autoComplete="new-password"
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              />
            </label>
            <label>
              Poznámka
              <input
                value={form.notes || ''}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={!!form.active}
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked ? 1 : 0 }))}
              />
              Aktivní
            </label>
            {editingId ? (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={!!form.rotate_api_token}
                  disabled={busy}
                  onChange={(e) => setForm((f) => ({ ...f, rotate_api_token: e.target.checked }))}
                />
                Vygenerovat nový API token
              </label>
            ) : null}
            {revealedToken ? (
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#0f766e' }}>
                API token (uložte si jej): <code>{revealedToken}</code>
              </p>
            ) : editingId ? (
              <p className="attendance-muted" style={{ margin: 0, fontSize: '0.8rem' }}>
                Token se zobrazí jen při vytvoření nebo po rotaci.
              </p>
            ) : null}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="system-button att-cat__btn" disabled={busy} onClick={save}>
                Uložit
              </button>
              {editingId ? (
                <button type="button" className="system-button att-cat__btn" disabled={busy} onClick={startCreate}>
                  Zrušit úpravu
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
